// Inventory — what the desk holds, what it cost, what it sold for.
//
// A report, not a workflow: read-only, dense, no stray buttons. (If actions are
// ever wanted here they must come from components/ActionBar.tsx, which is THE
// canonical action row — never hand-rolled buttons.)
//
// THE ONE RULE THIS SCREEN EXISTS TO MAKE VISIBLE
// (operator decision, 2026-08-13, verbatim: "INR & AED Trades keep seperate.
// derive avg. buying costs and avg. selling cost for all inventory.")
//
//     A book is one asset bought with one currency. USDT bought with AED and
//     USDT bought with INR are DIFFERENT books with different costs, and they
//     are never blended. Margin is realised when inventory is SOLD, and a
//     losing sale shows as a negative number.
//
// AND THE ONE THING THAT MUST NEVER BE QUIET
//     Where the cost came from. An assumed cost that reads like a measured one
//     turns an estimate into a believed profit. Every book carries its basis on
//     the row as a pill, and inventory the books carry no cost for at all is
//     the loudest thing on the page: sold as things stand, its whole sale
//     price would book as profit.
//
// Currency: per book. Quantities are in the asset, money is in that book's own
// quote currency. Nothing is converted anywhere.
//
// Layout:
//   lg+      one dense table, one row per book
//   below lg one block per book, same numbers, same order — the Ledger's
//            block head (the book's name; "margin" and the period's figure)
//            over the table's own cells as rows, two columns wide, and the
//            basis pill leading the block's closing line, as Profit's
//            currency blocks (19 Sept 2026: the card had a shape of its own
//            — 14px muted labels, the margin mid-card behind a rule — none
//            of it the report's). 21 Oct 2026: the pill is in the block's
//            head, beside the name, now (see the audit note below).
//
// 14 Sept 2026 (SWEEP-SCOPE §5.23): the period chips, table, pill and
// fmtRate are the shared pieces; the subtitle is the period alone; the
// mechanism sentences live in "About this screen"; the API path the unbased
// card printed at the operator is gone; the line under a row is "· 02 Sep ·
// why"; the headers are the one set Trading uses ("Avg cost · Avg sell ·
// Margin", qualifiers after a dot); and a period with no buys shows "—" under
// Avg cost with the lifetime figure beneath, as the sell column always did.
//
// 21 Oct 2026 audit (overnight clean view; operator: "I need clean view for
// operator"): the cost-basis pill — the one thing that must never be quiet —
// stands BESIDE the book's name, on the table and in the phone block's head,
// not in a last column 80–160px off the right edge or at the foot of a phone
// block. The BFF's amber "the cost is assumed…" note is not printed while a
// live book's own "Provisional" / "Assumed at peg" pill says it (the red "No
// cost behind …" line is the action and stays, and so does the pill on a book
// with no cost). The line under a book never opens or ends on a dot ("· 02 Sep
// · why" above). The quote currency is said once per row — in the book's
// name — not again after both margins; on the console the word "lifetime"
// labels the first lifetime average of a row, the figure beside it needs no
// second label.
//
// 21 Oct 2026 design-system sweep: a failed read keeps the title and the period chips and
// is a red line with "Try again" (it was a bare red paragraph with neither); the red
// no-cost line is "No cost behind 3.2 ETH — set an opening cost first."; a server note
// shows its first sentence with the rest behind "Details"; no books is the kit's one
// empty line; the word "lifetime" under a rate is text, only its figure is mono.

import { Fragment, useState, type ReactNode } from 'react';
import { ApiError, readWords } from '../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { request, type PnlPeriodKey } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { EmptyState, Pending, Pill, StateLine } from '../components/kit';
import type { PillTone } from '../components/Pill';
import { PeriodChips, reportLabel, reportPeriod, type PeriodValue } from '../components/PeriodChips';
import { ReportTable, type ReportRow, Figure } from '../components/ReportTable';
import { LedgerHead } from '../components/Ledger';
import { displayAsset, fmtAmount, fmtDay, fmtRate } from '../lib/format';

/* ── Response shape ────────────────────────────────────────────────────────
 * The canonical definition is documented above GET /inventory in
 * bff/src/routes/summary.ts, and that route's comment block ends with this
 * shape and the client method written out ready to paste into
 * mini-app/src/lib/api.ts. Both live here instead, because lib/api.ts belongs
 * to another workstream this build. When `api.inventory` lands, delete
 * everything down to the query and call it. */

type BasisSource = 'measured' | 'operator' | 'peg' | 'provisional' | 'unset';

interface InventoryBasis {
  source: BasisSource;
  label: string;
  assumed: boolean;
  caveat: string | null;
  rate: number | null;
  opening_quantity: number | null;
  set_at: string | null;
  note: string | null;
}

interface InventoryBook {
  key: string;
  asset: string;
  quote_currency: string;
  label: string;
  quantity: number | null;
  avg_buy_cost: number | null;
  avg_buy_cost_lifetime: number | null;
  avg_sell_price: number | null;
  avg_sell_price_period: number | null;
  book_value: number | null;
  realised_margin: number;
  realised_margin_lifetime: number;
  bought_quantity_lifetime: number;
  sold_quantity_lifetime: number;
  trades_in_period: number;
  last_trade_rate: number | null;
  last_trade_at: string | null;
  /** Emptied into its folded twin by the 2 Sept rekey (USDT·TRC · AED →
   *  USDT · AED) and kept for history only. Absent on an older worker, which
   *  reads as "live" — the safe direction: a book is never hidden by mistake. */
  retired?: boolean;
  basis: InventoryBasis;
}

interface InventoryUnbased {
  asset: string;
  label: string;
  quantity: number;
  held: number;
  on_books: number;
}

interface InventoryNote { code: string; tone: 'warn' | 'info'; message: string }

interface InventoryResponse {
  entity: string;
  period: { key: PnlPeriodKey; label: string; from: string | null; to: string; timezone: string };
  books: InventoryBook[];
  unbased: InventoryUnbased[];
  notes: InventoryNote[];
  meta: {
    minor_units: boolean;
    books_table: boolean;
    ledger_read: boolean;
    deals_rows: number;
  };
}

/**
 * GET /api/summary/inventory through lib/api.ts's one client: the same sign-in, the same ApiError and dead-session handling, and the bound (it was
 * a copy of request() until 26 Oct 2026). It goes the moment /api/summary/inventory has a method there.
 */
async function fetchInventory(period: PnlPeriodKey, days: { from?: string; to?: string }): Promise<InventoryResponse> {
  const qs = new URLSearchParams({ period });
  if (days.from) qs.set('from', days.from);
  if (days.to)   qs.set('to', days.to);
  return request<InventoryResponse>(`/api/summary/inventory?${qs}`);
}

/** A book's name, in the one wording (§6 "An inventory book"): "USDT · AED". */
function bookTitle(book: InventoryBook): string {
  return `${displayAsset(book.asset)} · ${displayAsset(book.quote_currency)}`;
}

/* ── Basis, the part that must never be quiet ─────────────────────────────── */

/** Short words for the row. The BFF sends a full sentence too (`basis.caveat`);
 *  this is the pill that catches the eye first. */
const BASIS_BADGE: Record<BasisSource, string> = {
  measured:    'Measured',
  operator:    'Your figure',
  peg:         'Assumed at peg',
  provisional: 'Provisional',
  unset:       'No cost set',
};

/** A measured cost is quiet; an assumption is warn; no cost at all is the
 *  negative tone, because a sale against it books the whole sale price as
 *  profit. */
const BASIS_TONE: Record<BasisSource, PillTone> = {
  measured:    'neutral',
  operator:    'info',
  peg:         'warn',
  provisional: 'warn',
  unset:       'bad',
};

function BasisPill({ basis, className = '' }: { basis: InventoryBasis; className?: string }) {
  return <Pill tone={BASIS_TONE[basis.source]} className={className}>{BASIS_BADGE[basis.source]}</Pill>;
}

/** The line under a row: "· 02 Sep · why" — the pill already carries the
 *  state, so the line carries only the date and the door to the BFF's full
 *  sentence and the operator's note (§5.23; four vocabularies for five states
 *  were the clutter). The red no-cost state keeps its whole sentence. */
function BasisLine({ basis, quote, withRate = false, lead, leadDot = false, className = '' }: {
  basis: InventoryBasis; quote: string; withRate?: boolean;
  /** Something the line opens with, inline — the table row's trade count. */
  lead?: ReactNode;
  /** A dot between the lead and what follows (a text lead; not a pill). */
  leadDot?: boolean; className?: string;
}) {
  const [why, setWhy] = useState(false);
  // "1.0002 · 02 Sep · why" — only the parts that exist, joined by dots, so the
  // line never opens or ends on one (it read "· 02 Sep · why"; 21 Oct 2026
  // audit).
  const bits: ReactNode[] = [];
  if (withRate && basis.rate !== null) bits.push(<span className="font-mono tabular">{fmtRate(basis.rate, quote)}</span>);
  if (basis.set_at) bits.push(<>{fmtDay(basis.set_at)}</>);
  bits.push(
    <button
      type="button"
      onClick={() => setWhy((v) => !v)}
      className="tap-44 underline decoration-dotted transition-colors hover:text-tg-text"
    >
      {why ? 'hide' : 'why'}
    </button>,
  );
  return (
    <div className={`text-meta text-tg-hint whitespace-normal ${className}`}>
      {lead}{lead && leadDot ? ' · ' : null}
      {bits.map((b, i) => <Fragment key={i}>{i > 0 && ' · '}{b}</Fragment>)}
      {why && (
        <p className="mt-1 leading-[1.5]">
          {basis.caveat}
          {basis.note && <> {basis.note}</>}
        </p>
      )}
    </div>
  );
}

/** A period rate with the lifetime figure on the line beneath when it
 *  differs — the same shape in BOTH average columns (§5.23 error-avoidance:
 *  the cost column used to show the lifetime average under the period header
 *  with no marker when the period had no buys). The lifetime line is a
 *  figure, not a hint: on the phone it stays at the cell's own 13px (the
 *  block's rows are the only place it is read), and steps down to 11px
 *  only in the dense desktop table, where it always sat.
 *  `labelled` is the word "lifetime" before the figure: on the console the
 *  two sub-lines of a row stand side by side, so only the first says it
 *  (21 Oct 2026 audit); a phone row stands alone and always does. */
function RateCell({ period, lifetime, quote, labelled = true }: { period: number | null; lifetime: number | null; quote: string; labelled?: boolean }) {
  const showLifetime = lifetime !== null && lifetime !== period;
  return (
    <>
      <span className="font-mono tabular">{fmtRate(period, quote)}</span>
      {showLifetime && (
        <div className="text-body lg:text-meta text-tg-hint">{labelled ? 'lifetime ' : ''}<span className="font-mono tabular">{fmtRate(lifetime, quote)}</span></div>
      )}
    </>
  );
}

/** The first sentence of a note or of what the books said; the rest is behind a tap (the sweep, 21 Oct 2026). */
function firstSentence(words: string): string {
  return words.split(/(?<=[.!?])\s+/)[0] ?? words;
}

/* ── Screen ───────────────────────────────────────────────────────────────── */

export function Inventory() {
  const [period, setPeriod] = useState<PeriodValue>({ key: 'month' });
  // Named periods are the BFF's own Dubai windows; only "Dates…" sends days.
  const { key: asked, ...days } = reportPeriod(period);   // Yesterday goes as its two days

  const q = useQuery({
    queryKey: ['inventory', asked, days.from ?? null, days.to ?? null],
    queryFn: () => fetchInventory(asked, days),
  });

  const data = q.data;
  const info    = data ? data.notes.filter((n) => n.tone !== 'warn') : [];
  const retired = data ? data.books.filter((b) => b.retired) : [];
  // The header stands in every state — a read that is slow or failed still lets the period be changed
  // (the sweep, 21 Oct 2026: a failed read was a bare red line with no title and no chips).
  const header = (
    <ScreenHeader
      title="Inventory"
      subtitle={reportLabel(period, data?.period.label)}
      help={
        <>
          {info.map((n) => <p key={n.code}>{n.message}</p>)}
          <p>
            Every row says where its cost came from. A measured cost is quiet; an assumed one is
            marked, and its margin is an estimate; inventory with no cost at all is the loudest
            thing on the page, because sold as things stand its whole sale price books as profit.
            Set an opening cost before trading it — the books take one opening cost per book,
            record who set it, and refuse it on a book that already holds a position.
          </p>
          {retired.length > 0 && (
            <p>Retired books were emptied into the folded books on 2 Sept and are kept for their history.</p>
          )}
        </>
      }
    >
      <PeriodChips value={period} onChange={setPeriod} allTime />
    </ScreenHeader>
  );

  // A FAILED READ is a line of its own — never "No books yet": what failed, the first sentence of what the
  // books said, the rest behind a tap, and the way out.
  if (q.isError) {
    const said = readWords(q.error);
    const first = firstSentence(said);
    return (
      <div className="space-y-4">
        {header}
        <StateLine
          tone="bad"
          more={said.slice(first.length).trim() || undefined}
          action={<button type="button" className="btn-link" onClick={() => q.refetch()}>Try again</button>}
        >
          Couldn&rsquo;t read inventory. {first}
        </StateLine>
      </div>
    );
  }
  if (!data) return <div className="space-y-4">{header}<Pending /></div>;

  // RETIRED BOOKS ARE HISTORY, NOT INVENTORY. The 2 Sept rekey emptied the
  // chain-keyed books (USDT·TRC · AED, USDT·ERC · AED, USDT·TRC · INR / USD)
  // into their folded twins and kept the old rows for their lifetime meters.
  // Shown in the main table they read as six dead positions beside the live
  // ones (6 Sept: twelve rows for five real books). They fold behind one line
  // with a count, the same way Treasury folds quiet accounts — a reading
  // affordance, never an accounting one: nothing is dropped, one tap opens it.
  const live    = data.books.filter((b) => !b.retired);
  // Amber notes carry something to act on and stay in view; the grey ones
  // ("Each book is one asset…") are how the screen works, and live behind
  // About this screen (7 Sept 2026).
  const warnings = data.notes.filter((n) => n.tone === 'warn');
  // The amber notes still printed. `inventory_without_cost` is the red line
  // under the header. `assumed_cost` ("BTC · AED: the cost is assumed, not
  // measured…") says what a live book's own pill already says ("Provisional",
  // "Assumed at peg"), so it is left off while such a pill is on screen (21 Oct
  // 2026 audit); if only a retired, folded book is assumed it still prints.
  const liveAssumed = live.some((b) => b.basis.assumed);
  const shownWarnings = warnings.filter((n) => n.code !== 'inventory_without_cost' && !(n.code === 'assumed_cost' && liveAssumed));

  return (
    <div className="space-y-4">
      {header}

      {/* Inventory with no cost behind it comes first, above everything. It is
          the number that would otherwise be read as free profit. ONE LINE
          (operator, 20 Sept 2026: "remove unnecessary errors & clutter" —
          a red box per asset, each with a sentence of mechanism, then the
          BFF's note saying the same in three sentences, then the same
          sentence again under every book): the holdings and the one
          instruction; the mechanism is in About. The BFF's own note on it
          (inventory_without_cost) is the same fact and is not printed. */}
      <UnbasedLine rows={data.unbased} />

      {shownWarnings.length > 0 && (
        <div className="space-y-1">
          {shownWarnings.map((n) => {
            const first = firstSentence(n.message);
            return <StateLine key={n.code} tone="warn" more={n.message.slice(first.length).trim() || undefined}>{first}</StateLine>;
          })}
        </div>
      )}

      {live.length === 0 ? (
        <EmptyState>No books yet.</EmptyState>
      ) : (
        <>
          {/* ── lg+: one dense table ─────────────────────────────────────── */}
          <div className="hidden lg:block">
            <BookTable books={live} />
          </div>

          {/* ── below lg: one block per book ─────────────────────────────── */}
          <div className="lg:hidden">
            {live.map((b) => <BookBlock key={b.key} book={b} />)}
          </div>
        </>
      )}

      {retired.length > 0 && <RetiredBooks books={retired} />}
    </div>
  );
}

/** The books the rekey emptied, behind one line. Same rows and blocks as
 *  the live books once opened — their lifetime meters are the reason they
 *  were kept — so nothing here is a second way of drawing a book. */
function RetiredBooks({ books }: { books: InventoryBook[] }) {
  const [open, setOpen] = useState(false);
  const n = books.length;
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-1 py-2 text-left text-meta text-tg-hint transition-colors hover:text-tg-text"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {open ? `Hide ${n} retired ${n === 1 ? 'book' : 'books'}` : `+ ${n} retired ${n === 1 ? 'book' : 'books'}`}
      </button>
      {open && (
        <>
          <div className="hidden lg:block">
            <BookTable books={books} />
          </div>
          <div className="lg:hidden">
            {books.map((b) => <BookBlock key={b.key} book={b} />)}
          </div>
        </>
      )}
    </section>
  );
}

/** Held, but on no book — one line: the holdings, and the one instruction. */
function UnbasedLine({ rows }: { rows: InventoryUnbased[] }) {
  if (rows.length === 0) return null;
  return (
    // ONE red line: the holdings and the one instruction (the sweep, 21 Oct 2026: 12 words with one asset, and an
    // icon the colour and the words already say). Why — sold as things stand its whole sale price books as profit —
    // is in About, where it always was.
    <StateLine tone="bad">
      No cost behind{' '}
      {rows.map((r, i) => (
        // a holding is one piece, the dot trails it — a wrap never starts a line on a dot
        <span key={r.asset} className="whitespace-nowrap">
          <span className="font-mono tabular">{fmtAmount(r.quantity, r.asset)}</span> {displayAsset(r.asset)}
          {i < rows.length - 1 && <span className="text-tg-hint"> ·</span>}{' '}
        </span>
      ))}
      — set an opening cost first.
    </StateLine>
  );
}

/* ════════════════ The cells — one builder for both forms ════════════════ */

/** One book's figures, the same cells in both forms: the desktop table's
 *  columns and the phone block's rows — one builder, as Profit's reportRows,
 *  so the two forms can never disagree on a figure, a weight or a code. The
 *  book's name and its basis are seated by each form itself: a name column
 *  carrying its pill and opening cost at lg+; the block's head (name and
 *  pill) and its closing line on the phone.
 *
 *  21 Oct 2026 audit: the book's quote currency is said ONCE per row, in its
 *  name ("USDT · AED") — the margins no longer repeat it. (The code was a third
 *  "AED" on every row.) On the console `lifetimeOnce` labels only the first of
 *  a row's lifetime averages; a phone row stands alone and keeps its own. */
function bookCells(book: InventoryBook, { lifetimeOnce = false }: { lifetimeOnce?: boolean } = {}): Record<'held' | 'cost' | 'sell' | 'margin' | 'lifetime', ReactNode> {
  const costShowsLifetime = book.avg_buy_cost_lifetime !== null && book.avg_buy_cost_lifetime !== book.avg_buy_cost;
  return {
    held:     <Figure value={book.quantity} asset={book.asset} />,
    cost:     <RateCell period={book.avg_buy_cost} lifetime={book.avg_buy_cost_lifetime} quote={book.quote_currency} />,
    sell:     <RateCell period={book.avg_sell_price_period} lifetime={book.avg_sell_price} quote={book.quote_currency} labelled={!(lifetimeOnce && costShowsLifetime)} />,
    margin:   <Figure value={book.realised_margin} asset={book.quote_currency} tone={marginTone(book)} strong />,
    lifetime: <Figure value={book.realised_margin_lifetime} asset={book.quote_currency} tone={marginTone(book)} />,
  };
}

/** A margin is green or red only when its cost is KNOWN. Against an assumed or an unset cost it is an estimate —
 *  neutral, beside the Provisional / No cost set pill (Astra review, 21 Oct 2026: a green lifetime margin on a book
 *  labelled Provisional, with the amber note that said why no longer printed). */
const marginTone = (book: InventoryBook): 'none' | 'signed' => (book.basis.assumed || book.basis.source === 'unset' ? 'none' : 'signed');

/** "64 trades" / "no trades this period" — the muted note under a book's name. */
function tradesNote(book: InventoryBook): string {
  return book.trades_in_period === 0
    ? 'no trades this period'
    : `${book.trades_in_period.toLocaleString('en-US')} ${book.trades_in_period === 1 ? 'trade' : 'trades'}`;
}

/* ════════════════ Desktop — the table ════════════════ */

// THE COST BASIS IS BESIDE THE NAME (21 Oct 2026 audit): it was the last
// column — 80px off the edge at 960px, 160px at 880 — and the pill that must
// never be quiet was the thing scrolled away. The name column carries it now,
// with the opening cost where there is one.
const COLUMNS = [
  { key: 'book',     label: 'Book', align: 'left' as const, width: '34%', nowrap: false },
  { key: 'held',     label: 'On hand' },
  { key: 'cost',     label: 'Avg cost' },
  { key: 'sell',     label: 'Avg sell' },
  { key: 'margin',   label: 'Margin' },
  { key: 'lifetime', label: 'Margin · lifetime' },
];

function BookTable({ books }: { books: InventoryBook[] }) {
  const rows: ReportRow[] = books.map((book) => {
    const { basis } = book;
    return {
      key: book.key,
      cells: {
        book: (
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium">{bookTitle(book)}</span>
              <BasisPill basis={basis} />
              {basis.rate !== null && (
                <span className="text-meta text-tg-hint font-mono tabular">{fmtRate(basis.rate, book.quote_currency)}</span>
              )}
            </div>
            {/* Under the row: "64 trades · 02 Sep · why". The no-cost state
                carried its sentence here on every such book — nine times on
                one screen (20 Sept 2026); its red pill says it, and the
                mechanism is in About. */}
            {basis.caveat && basis.source !== 'unset'
              ? <BasisLine basis={basis} quote={book.quote_currency} lead={tradesNote(book)} leadDot />
              : <div className="text-meta text-tg-hint">{tradesNote(book)}</div>}
          </div>
        ),
        ...bookCells(book, { lifetimeOnce: true }),
      },
    };
  });
  return <ReportTable columns={COLUMNS} rows={rows} empty="No books yet." />;
}

/* ════════════════ Phone — one block per book ════════════════ */

/** The block the phone reads, as Profit's currency blocks: the Ledger's
 *  block header — the book's name at the left, "margin" and the period's
 *  figure with its currency at the right, so the answer is on the first
 *  line of every block as the eye scrolls — then the table's own cells as
 *  rows, two columns wide, headless and unframed, and the basis closing the
 *  block: its pill leading the line the desktop row carries beneath it
 *  ("Measured · 3.6725 · 02 Sep · why"; the no-cost state is its pill). The pill is not in the head because the head is ONE
 *  line: name, the word and a figure with its code already fill 360px, and
 *  a pill there wrapped the figure under the word. The head and the closing
 *  line take the table's cell inset (px-4) so the name stands over the
 *  words and the figure over the figures, on one x. The word column wraps
 *  rather than scrolls.
 *
 *  21 Oct 2026 audit — the paragraph above is superseded in two places: the
 *  pill IS in the head now (beside the name, as on the table), because the
 *  margin no longer carries its currency — the name says it — which gives the
 *  head the room, and the title wraps the pill under the name if it still
 *  does not fit; and the closing line holds only what stands behind the pill
 *  (the opening cost, its date, "why"), or nothing. */
function BookBlock({ book }: { book: InventoryBook }) {
  const { basis } = book;
  const cells = bookCells(book);
  const rows: ReportRow[] = [
    {
      key: 'held',
      cells: {
        line: 'On hand',
        // The head names the book, not the asset alone, so the quantity
        // carries its unit here.
        fig: <>{cells.held}<span className="ml-1.5 text-meta text-tg-hint">{displayAsset(book.asset)}</span></>,
      },
    },
    { key: 'cost',     cells: { line: 'Avg cost',          fig: cells.cost } },
    { key: 'sell',     cells: { line: 'Avg sell',          fig: cells.sell } },
    { key: 'lifetime', cells: { line: 'Margin · lifetime', fig: cells.lifetime } },
  ];
  return (
    <section className="pt-3 pb-1">
      <LedgerHead
        className="px-4"
        title={
          <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {bookTitle(book)}
            <BasisPill basis={basis} />
          </span>
        }
        figure={<Figure value={book.realised_margin} asset={book.quote_currency} tone={marginTone(book)} />}
        note="margin"
        noteFirst
      />
      <ReportTable
        head={false}
        frame={false}
        columns={[
          { key: 'line', label: '', align: 'left', nowrap: false },
          { key: 'fig',  label: '' },
        ]}
        rows={rows}
      />
      {/* What stands behind the pill, closing the block: the opening cost,
          its date and the door to its reasons — only what there is. A book
          with no cost keeps "Cost used" where the books carry one. */}
      {(basis.source === 'unset' ? basis.rate !== null : !!basis.caveat) && (
        <div className="px-4 pt-1.5">
          {basis.source === 'unset'
            ? (
              <p className="text-meta leading-[1.5] text-tg-hint">
                Cost used <span className="font-mono tabular">{fmtRate(basis.rate, book.quote_currency)}</span>
              </p>
            )
            : <BasisLine basis={basis} quote={book.quote_currency} withRate />}
        </div>
      )}
    </section>
  );
}
